import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import KpiCard from '../components/KpiCard';
import './Dashboard.css';

function parseApiMessage(err, resData) {
  if (resData && typeof resData.message === 'string' && resData.message) return resData.message;
  const d = err?.response?.data;
  if (d && typeof d.message === 'string' && d.message) return d.message;
  if (typeof d === 'string' && d) return d;
  if (err?.message) return err.message;
  return 'Request failed';
}

const RECENT_PAGE_SIZE = 10;

/** Second line under Token / Voucher chip in dashboard table */
function rewardSubtitle(item) {
  if (item.rewardType === 'token' && item.rewardDetails) {
    const d = item.rewardDetails;
    const amt = d.tokenAmount != null && d.tokenAmount !== '' ? String(d.tokenAmount) : '';
    const label = (d.tokenName || d.tokenSymbol || '').trim();
    const combined = [amt, label].filter(Boolean).join(' ');
    return combined || '—';
  }
  if (item.rewardType === 'voucher' && item.rewardDetails) {
    return (item.rewardDetails.voucherName || '').trim() || '—';
  }
  return null;
}

function canResendReward(item) {
  return Boolean(item?.id && item.rewardType && !item.rewardSent && item.rewardSendError);
}

function DashboardPageHeader({ subtitle }) {
  return (
    <div className="dashboard-page-header">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>{subtitle}</p>
      </div>
      <Link to="/settings" className="btn btn-primary dashboard-header-settings">
        Settings
      </Link>
    </div>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentFetching, setRecentFetching] = useState(false);
  const [error, setError] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [actionBanner, setActionBanner] = useState(null);
  const [recentPage, setRecentPage] = useState(1);
  const hasLoadedOnceRef = useRef(false);
  const skipPageFetchRef = useRef(false);

  const fetchDashboard = useCallback(async () => {
    try {
      setError(null);
      if (hasLoadedOnceRef.current) {
        setRecentFetching(true);
      } else {
        setLoading(true);
      }
      const res = await api.get('/checkin/dashboard', {
        params: { page: recentPage, pageSize: RECENT_PAGE_SIZE },
      });
      if (res.data.status === 'Success') {
        const msg = res.data.message;
        setData(msg);
        hasLoadedOnceRef.current = true;
        const srvPage = msg.recentCheckinsPage;
        if (typeof srvPage === 'number' && srvPage !== recentPage) {
          skipPageFetchRef.current = true;
          setRecentPage(srvPage);
        }
      } else {
        setError(res.data.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
      setRecentFetching(false);
    }
  }, [recentPage]);

  useEffect(() => {
    if (skipPageFetchRef.current) {
      skipPageFetchRef.current = false;
      return;
    }
    fetchDashboard();
  }, [recentPage, fetchDashboard]);

  const handleResendReward = async (recordId) => {
    try {
      setResendingId(recordId);
      setActionBanner(null);
      const res = await api.post('/checkin/records/resend-reward', { recordId });
      if (res.data.status === 'Success') {
        setActionBanner({ type: 'ok', text: 'Reward sent successfully.' });
        await fetchDashboard();
      } else {
        setActionBanner({
          type: 'err',
          text: res.data.message || 'Resend failed',
        });
      }
    } catch (err) {
      setActionBanner({
        type: 'err',
        text: parseApiMessage(err, null),
      });
    } finally {
      setResendingId(null);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <DashboardPageHeader subtitle="Daily Check-in performance overview" />
        <div className="loading-container">
          <div className="spinner" />
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <DashboardPageHeader subtitle="Daily Check-in performance overview" />
        <div className="dash-error">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchDashboard}>Retry</button>
        </div>
      </div>
    );
  }

  const {
    dailyCheckinCount = 0,
    totalUsers = 0,
    todayDate,
    recentCheckins = [],
    recentCheckinsTotal = 0,
    recentCheckinsPageSize = RECENT_PAGE_SIZE,
    canResendRewards = false,
  } = data || {};

  const totalRecentPages =
    recentCheckinsTotal === 0
      ? 1
      : Math.max(1, Math.ceil(recentCheckinsTotal / recentCheckinsPageSize));

  const goPrevRecent = () => setRecentPage((p) => Math.max(1, p - 1));
  const goNextRecent = () => setRecentPage((p) => Math.min(totalRecentPages, p + 1));

  return (
    <div className="dashboard">
      <DashboardPageHeader
        subtitle={`Daily Check-in performance overview${todayDate ? ` — ${todayDate}` : ''}`}
      />

      <div className="kpi-grid">
        <KpiCard
          title="Today's Check-ins"
          value={dailyCheckinCount}
          icon="✅"
          color="indigo"
          subtitle={`${totalUsers} total users`}
        />
      </div>

      <div className="card recent-section">
        <div className="recent-header">
          <div className="recent-header-main">
            <h2>Check-In Records</h2>
            <span className="recent-count recent-count--below-title">
              {recentCheckinsTotal}{' '}
              {recentCheckinsTotal === 1 ? 'record' : 'records'}
            </span>
          </div>
          <div className="recent-header-actions">
            <button
              className="btn btn-secondary btn-sm"
              onClick={fetchDashboard}
              disabled={recentFetching}
            >
              {recentFetching ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {actionBanner && (
          <div
            className={`dash-action-banner dash-action-banner-${actionBanner.type}`}
            role="status"
          >
            {actionBanner.text}
            <button
              type="button"
              className="dash-action-banner-dismiss"
              onClick={() => setActionBanner(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {recentCheckins.length === 0 && !recentFetching ? (
          <div className="empty-state">
            <p>No check-in records yet.</p>
          </div>
        ) : (
          <div
            className={`table-wrapper${recentFetching ? ' table-wrapper--loading' : ''}`}
          >
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Reward</th>
                  <th>Status</th>
                  <th>Time</th>
                  {canResendRewards ? <th className="actions-col">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {recentCheckins.map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td>
                      <span className="user-cell">
                        {item.displayName || item.username || item.userName || '—'}
                      </span>
                    </td>
                    <td>{item.date || '—'}</td>
                    <td>
                      <span className="day-badge">Day {item.rewardDay || '—'}</span>
                    </td>
                    <td>
                      {item.rewardType === 'token' && item.rewardDetails ? (
                        <div className="reward-cell-stack">
                          <span className="badge badge-token">Token</span>
                          <div className="reward-name-line">{rewardSubtitle(item)}</div>
                        </div>
                      ) : item.rewardType === 'voucher' && item.rewardDetails ? (
                        <div className="reward-cell-stack">
                          <span className="badge badge-voucher">Voucher</span>
                          <div className="reward-name-line">{rewardSubtitle(item)}</div>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <div className="reward-status-cell">
                        {item.rewardSent ? (
                          <span className="badge badge-sent">Sent</span>
                        ) : item.rewardType ? (
                          <>
                            <span
                              className={`badge ${
                                item.rewardSendError ? 'badge-failed' : 'badge-pending'
                              }`}
                            >
                              {item.rewardSendError ? 'Failed' : 'Pending'}
                            </span>
                            {item.rewardSendError ? (
                              <div
                                className="reward-send-error-line"
                                title={item.rewardSendError}
                              >
                                {item.rewardSendError}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          '—'
                        )}
                      </div>
                    </td>
                    <td className="time-cell">
                      {item.checkedInAt
                        ? new Date(item.checkedInAt).toLocaleString()
                        : '—'}
                    </td>
                    {canResendRewards ? (
                      <td className="actions-col">
                        {canResendReward(item) ? (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={resendingId === item.id}
                            onClick={() => handleResendReward(item.id)}
                          >
                            {resendingId === item.id ? 'Sending…' : 'Resend'}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
            {recentCheckinsTotal > 0 && (
              <div className="recent-pagination">
                <span className="recent-page-info">
                  Page {recentPage} of {totalRecentPages}
                </span>
                <div className="recent-pagination-nav">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={goPrevRecent}
                    disabled={recentFetching || recentPage <= 1}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={goNextRecent}
                    disabled={recentFetching || recentPage >= totalRecentPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
